import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../store/store';
import authService from '../services/authService';
import { UserRole } from '../types/user';
import { login } from '../store/authSlice';

// Fonction utilitaire pour vérifier si l'utilisateur est connecté
const isUserAuthenticated = () => {
  if (typeof window !== 'undefined') {
    return !!localStorage.getItem('user');
  }
  return false;
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const fcDashboardUrl = process.env.REACT_APP_FC_DASHBOARD_URL || 'https://appstore.footballcoachs.com';
  
  // Surveiller l'état de la connexion Internet
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Vérifier l'authentification via localStorage
  const isAuthenticated = isUserAuthenticated();
  
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      setError(null);
      setSuccessMessage(null);
      
      if (isRegisterMode) {
        // Mode inscription
        if (!email || !password || !firstName || !lastName) {
          setError('Veuillez remplir tous les champs');
          return;
        }
        
        if (password.length < 6) {
          setError('Le mot de passe doit contenir au moins 6 caractères');
          return;
        }
        
        // Créer un nouvel utilisateur
        try {
          const result = await authService.register({ email, password, firstName, lastName });
          if (!result) {
            setError('Erreur lors de la création du compte');
            return;
          }

          localStorage.setItem('user', JSON.stringify(result.user));
          localStorage.setItem('token', result.token);

          if (result.user.status && result.user.status !== 'active') {
            navigate('/onboarding');
          } else {
            navigate('/');
          }
        } catch (error) {
          setError('Erreur lors de la création du compte');
        }
      } else {
        // Mode connexion
        if (!email || !password) {
          setError('Veuillez remplir tous les champs');
          return;
        }
        
        // Vérifier la connexion Internet
        if (!navigator.onLine) {
          setError('Vous êtes actuellement hors ligne. Veuillez vous connecter à Internet pour continuer.');
          return;
        }
        
        // Utiliser l'action Redux pour la connexion
        try {
          const result = await dispatch(login({ email, password }));
          if (result.meta.requestStatus === 'fulfilled') {
            const payload = (result as { payload?: unknown }).payload as
              | { user?: { status?: string } }
              | undefined;
            if (payload?.user?.status && payload.user.status !== 'active') {
              navigate('/onboarding');
            } else {
              navigate('/');
            }
          } else {
            setError('Identifiants invalides.');
          }
        } catch (error) {
          console.error('Erreur de connexion:', error);
          setError('Erreur de connexion au serveur. Veuillez réessayer.');
        }
      }
    } catch (error) {
      console.error('Erreur lors de la soumission du formulaire:', error);
      if (!navigator.onLine) {
        setError('Vous êtes actuellement hors ligne. Veuillez vous connecter à Internet pour continuer.');
      } else {
        setError('Erreur de connexion au serveur. Veuillez réessayer.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenResetModal = () => {
    setResetEmail(email);
    setIsResetModalOpen(true);
    setError(null);
    setSuccessMessage(null);
  };

  const handleCloseResetModal = () => {
    setIsResetModalOpen(false);
  };

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!resetEmail) {
      setError('Veuillez renseigner votre adresse email.');
      return;
    }

    if (!navigator.onLine) {
      setError('Vous êtes actuellement hors ligne. Veuillez vous connecter à Internet pour continuer.');
      return;
    }

    setIsLoading(true);
    try {
      const ok = await authService.requestPasswordReset(resetEmail);
      if (!ok) {
        setError('Impossible d\'envoyer l\'email de réinitialisation. Vérifie l\'adresse et réessaie.');
        return;
      }
      setSuccessMessage('Email de réinitialisation envoyé. Vérifie ta boîte de réception.');
      setIsResetModalOpen(false);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Rediriger si déjà connecté
  if (isAuthenticated) {
    return <Navigate to="/" />;
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="bg-white/90 backdrop-blur rounded-2xl shadow-xl ring-1 ring-black/5 p-6 sm:p-8">
          <div className="flex flex-col items-center text-center">
            <div className="h-12 w-12 rounded-2xl bg-livematch-primary/10 flex items-center justify-center mb-3">
              <span className="text-livematch-primary font-extrabold text-lg">LM</span>
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">
              {isRegisterMode ? 'Créer un compte' : 'Connexion'}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {isRegisterMode ? 'Crée ton accès en quelques secondes.' : 'Accède à ton espace LiveMatch.'}
            </p>
          </div>

          {!isRegisterMode && (
            <div className="mt-6 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700">
              <div className="text-sm font-semibold">Compte Football Coachs ?</div>
              <div className="mt-0.5 text-sm opacity-90">
                Connecte-toi via le tableau de bord pour accéder automatiquement à LiveMatch.
              </div>
              <a
                href={fcDashboardUrl}
                className="mt-2 inline-flex text-sm font-semibold text-livematch-primary hover:underline"
              >
                Ouvrir le tableau de bord
              </a>
            </div>
          )}
        
        {!isOnline && (
          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900" role="alert">
            <div className="text-sm font-semibold">Vous êtes hors ligne</div>
            <div className="text-sm opacity-90">Une connexion Internet est nécessaire pour continuer.</div>
          </div>
        )}
        
        {error && (
          <div className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-900" role="alert">
            <div className="text-sm">{error}</div>
          </div>
        )}
        
        {successMessage && (
          <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-900" role="alert">
            <div className="text-sm">{successMessage}</div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          {isRegisterMode && (
            <>
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-slate-700 mb-1">
                  Prénom
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  required
                  autoComplete="given-name"
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 shadow-sm outline-none transition focus:border-livematch-primary focus:ring-4 focus:ring-livematch-primary/15"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-slate-700 mb-1">
                  Nom
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  required
                  autoComplete="family-name"
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 shadow-sm outline-none transition focus:border-livematch-primary focus:ring-4 focus:ring-livematch-primary/15"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </>
          )}
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
              Adresse email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              autoComplete="email"
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 shadow-sm outline-none transition focus:border-livematch-primary focus:ring-4 focus:ring-livematch-primary/15"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">
              Mot de passe
            </label>
            <input
              type="password"
              id="password"
              name="password"
              required
              autoComplete={isRegisterMode ? 'new-password' : 'current-password'}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 shadow-sm outline-none transition focus:border-livematch-primary focus:ring-4 focus:ring-livematch-primary/15"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {!isRegisterMode && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleOpenResetModal}
                className="text-sm font-semibold text-livematch-primary hover:underline focus:outline-none"
              >
                Mot de passe oublié ?
              </button>
            </div>
          )}
          
          <button
            type="submit"
            disabled={isLoading}
            className="w-full rounded-xl bg-livematch-primary text-white py-2.5 px-4 font-semibold shadow-sm transition hover:opacity-95 focus:outline-none focus:ring-4 focus:ring-livematch-primary/20 disabled:opacity-50"
          >
            {isLoading ? (isRegisterMode ? 'Création en cours...' : 'Connexion en cours...') : (isRegisterMode ? 'Créer un compte' : 'Se connecter')}
          </button>
        </form>
        
        <div className="mt-6 text-center">
          <button 
            onClick={() => {
              setIsRegisterMode(!isRegisterMode);
              setError(null);
              setSuccessMessage(null);
            }}
            className="text-sm font-semibold text-livematch-primary hover:underline focus:outline-none"
          >
            {isRegisterMode ? 'Déjà un compte ? Se connecter' : 'Pas encore de compte ? S\'inscrire'}
          </button>
        </div>

        <div className="mt-6 text-center text-xs text-slate-400">
          En te connectant, tu acceptes les conditions d'utilisation.
        </div>
        </div>
      </div>

      {isResetModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/40" onClick={handleCloseResetModal} />
          <div className="relative w-full max-w-md rounded-2xl bg-white shadow-xl ring-1 ring-black/10 p-6">
            <div className="text-lg font-semibold text-slate-900">Réinitialiser le mot de passe</div>
            <div className="mt-1 text-sm text-slate-500">
              Renseigne ton email. Tu recevras un lien pour choisir un nouveau mot de passe.
            </div>

            <form onSubmit={handleResetPassword} className="mt-5 space-y-4">
              <div>
                <label htmlFor="resetEmail" className="block text-sm font-medium text-slate-700 mb-1">
                  Adresse email
                </label>
                <input
                  type="email"
                  id="resetEmail"
                  name="resetEmail"
                  required
                  autoComplete="email"
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 shadow-sm outline-none transition focus:border-livematch-primary focus:ring-4 focus:ring-livematch-primary/15"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleCloseResetModal}
                  className="flex-1 rounded-xl border border-slate-200 bg-white py-2.5 px-4 font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-200"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 rounded-xl bg-livematch-primary text-white py-2.5 px-4 font-semibold shadow-sm transition hover:opacity-95 focus:outline-none focus:ring-4 focus:ring-livematch-primary/20 disabled:opacity-50"
                >
                  Envoyer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
